import { CoreInput } from '@/components/Form/InputAntForm';
import CoreSelect from '@/components/Form/SelectAntForm';
import { TimeCell } from '@/components/TimeCell';
import { useHandleActionItem } from '@/components/hooks/useHandleActionItem';
import { useCoreContext, useOpacity } from '@/core';
import { BusinessUnitTable, CategoryEntity, ProductGroupEntity } from '@/domains/business-unit/entities';
import { EditOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { Tag, Typography } from 'antd';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import { get } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';

export const BusinessUnitTableColumns = () => {
  const { t } = useTranslation();
  const { control, businessUnitList, categoryList, productGroups } = useCoreContext();
  const { opacity, setOpacity } = useOpacity();
  const { handleClickEdit } = useHandleActionItem();

  const optionsStatus = [
    {
      value: 'ACTIVE',
      label: 'ACTIVE',
    },
    {
      value: 'INACTIVE',
      label: 'INACTIVE',
    },
  ];

  const getColumnSearchProps = (): ColumnType<BusinessUnitTable> => ({
    filterDropdown: () => (
      <div style={{ padding: 8, width: '200px' }} onKeyDown={(e) => e.stopPropagation()}>
        <CoreInput control={control} name='search' />
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <>
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      </>
    ),
  });

  const getColumnStatusProps = (): ColumnType<BusinessUnitTable> => ({
    filterDropdown: () => (
      <div style={{ padding: 8, width: '200px' }} onKeyDown={(e) => e.stopPropagation()}>
        <CoreSelect control={control} name='status' optionsProps={optionsStatus} />
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <>
        <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      </>
    ),
  });

  const renderIdTags = (
    ids: string[] | undefined,
    sourceList: { id: string; name: string }[] | undefined,
    color: string,
  ) => {
    if (!Array.isArray(ids) || ids.length === 0 || !Array.isArray(sourceList)) {
      return null;
    }
    const matched = sourceList.filter((item) => ids.includes(item.id));
    if (matched.length === 0) {
      return null;
    }
    return matched.map((item) => (
      <Tag color={color} key={item.id}>
        {item.name}
      </Tag>
    ));
  };

  const tableColumns: ColumnsType<BusinessUnitTable> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '180px',
      ...getColumnSearchProps(),
    },
    {
      title: t('common.businessUnitChildren'),
      dataIndex: 'childrenIds',
      key: 'childrenIds',
      width: '160px',
      render: (childrenIds: string[]) =>
        renderIdTags(childrenIds, businessUnitList, 'purple'),
    },
    {
      title: 'Business Domain',
      dataIndex: 'businessDomainId',
      key: 'businessDomainId',
      width: '160px',
      render: (value: string, record: BusinessUnitTable) => {
        return get(record, 'businessDomain.name') ?? value ?? null;
      },
    },
    {
      title: t('common.category'),
      dataIndex: 'categoryIds',
      key: 'categoryIds',
      width: '140px',
      render: (categoryIds: string[]) =>
        renderIdTags(categoryIds, categoryList as CategoryEntity[], 'blue'),
    },
    {
      title: t('common.offeringProductGroup'),
      dataIndex: 'offeringProductGroupIds',
      key: 'offeringProductGroupIds',
      width: '160px',
      render: (offeringProductGroupIds: string[]) =>
        renderIdTags(offeringProductGroupIds, productGroups as ProductGroupEntity[], 'green'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '120px',
      render: (value: string) => {
        if (value === 'ACTIVE') {
          return <Typography.Text type='success'>Active</Typography.Text>;
        }
        return <Typography.Text disabled>Deactivated</Typography.Text>;
      },
      ...getColumnStatusProps(),
    },
    {
      title: 'Last update at',
      dataIndex: 'lastModifiedAt',
      key: 'lastModifiedAt',
      width: '160px',
      render: (value: number) => <TimeCell value={value} />,
    },
    {
      title: 'Action',
      width: '6rem',
      key: 'action',
      render: (record: any) => {
        return (
          <div
            className='actions'
            onMouseOver={() => setOpacity(1)}
            onMouseLeave={() => setOpacity(0.6)}
          >
            <EditOutlined
              style={{ color: 'blue', cursor: 'pointer', opacity }}
              onClick={() => handleClickEdit(record)}
            />
          </div>
        );
      },
      fixed: 'right',
    },
  ];

  return tableColumns;
};
